<template>
  <b-modal
    id="modal-batch-approval-list"
    ref="modalBatchApprovalList"
    title="Select Batch for Approval"
    size="xl"
    hide-footer
    @shown="fetchBatches"
    @hidden="resetModal">
    <div>
      <!-- Course Info Header -->
      <div class="mb-4">
        <h5 class="mb-2">{{ selectedCourse?.title }}</h5>
        <p class="text-muted mb-0">{{ selectedCourse?.description }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="batchLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading batches...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="batches.length === 0" class="text-center py-5">
        <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No batches available</p>
      </div>

      <!-- Batch Table -->
      <div v-else>
        <vue-good-table
          :columns="batchColumns"
          :rows="batches"
          :pagination-options="{ enabled: true }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'batch'">
              {{ formatBatchLabel(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'penyelenggara'">
              {{ formatPenyelenggara(props.row.penyelenggara) }}
            </span>
            <span v-else-if="props.column.field === 'tanggal'">
              {{ formatBatchDate(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'batch_status'">
              <span class="badge" :class="getBatchStatusClass(props.row.batch_status)">
                {{ props.row.batch_status }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <button class="btn btn-sm btn-outline-primary" @click="selectBatch(props.row, props.index)">
                <i class="fe fe-users me-1"></i> View
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { formatBatchLabel, formatPenyelenggara } from '@/utils/batchIdentity';

export default {
  name: 'ModalBatchApprovalList',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    idpPeriod: {
      type: [Number, String],
      default: null
    },
    company: {
      type: String,
      default: null
    }
  },
  components: {
    VueGoodTable
  },
  computed: {
    ...mapGetters('mitraUnenrollmentReport', ['batches', 'batchLoading'])
  },
  data() {
    return {
      batchColumns: [
        { label: 'Batch', field: 'batch' },
        { label: 'Penyelenggara', field: 'penyelenggara', width: '180px' },
        { label: 'Tanggal', field: 'tanggal', width: '220px' },
        { label: 'Status', field: 'batch_status' },
        { label: 'Quota', field: 'quota' },
        { label: 'Enrolled', field: 'enrolled' },
        { label: 'Available', field: 'available_quota' },
        { label: 'Participants', field: 'action', width: '150px' }
      ]
    };
  },
  methods: {
    formatBatchLabel,
    formatPenyelenggara,
    async fetchBatches() {
      if (!this.selectedCourse?.id) return;
      
      try {
        const params = {};
        if (this.idpPeriod) {
          params.idp_period = this.idpPeriod;
        }
        if (this.company) {
          params.company = this.company;
        }

        await this.$store.dispatch('mitraUnenrollmentReport/getBatchesApproval', {
          courseId: this.selectedCourse.id,
          params
        });
      } catch (error) {
        console.error('Error fetching batches:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Daftar batch belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    resetModal() {
      // Reset any local state if needed
    },
    
    selectBatch(batch, index) {
      this.$emit('batch-selected', { batch, index });
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    formatBatchDate(batch) {
      const startText = this.formatDate(batch?.start_date);
      const endText = this.formatDate(batch?.end_date);

      if (startText !== '-' || endText !== '-') {
        if (startText !== '-' && endText !== '-' && startText !== endText) {
          return `${startText} - ${endText}`;
        }
        return startText !== '-' ? startText : endText;
      }

      const schedules = Array.isArray(batch?.schedules) ? batch.schedules : [];
      const validDates = schedules
        .map((schedule) => schedule?.date)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()))
        .sort((first, second) => first - second);

      if (!validDates.length) return '-';

      const firstText = this.formatDate(validDates[0]);
      const lastText = this.formatDate(validDates[validDates.length - 1]);
      return firstText === lastText ? firstText : `${firstText} - ${lastText}`;
    },

    formatDate(value) {
      if (!value) return '-';

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '-';

      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    }
  }
};
</script>

<style scoped>
.me-1 {
  margin-right: 0.25rem;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}
</style>
